/*
 * File: FormBuilder.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: HOC called from another component to build out a form based on the model data
 * provided in the prop
 */

import React, { Component } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Button, Grid } from "@material-ui/core";
import FormFields from "./components/FormFields";
import UIAlertDialog from "../ui/UIAlertDialog";
import { withStyles } from "@material-ui/core";

const styles = theme => ({
  apply: {
    color: theme.palette.common.white
  }
});

class FormBuilder extends Component {
  state = {
    errorMessageOpen: false
  };

  handleClose = () => {
    this.setState({ errorMessageOpen: false });
  };

  /**
   * Submit the form
   *
   * @param {Object} e: Form dom object
   */
  onSubmit = e => {
    e.preventDefault();
    e.persist();

    if (typeof this.props.customValidate === "function") {
      this.props.customValidate(this.props.formStore);
      if (this.props.formStore.formError)
        this.setState({ errorMessageOpen: true });
    }

    this.props.formStore.validateAllFields(this.props.data);

    // call parent submit if applicable
    if (
      this.props.formStore.isValid() &&
      typeof this.props.parentOnSubmit === "function"
    ) {
      this.props.parentOnSubmit(e);
    }
  };

  /**
   * Determines whether or not to show a cancel button
   *
   * @return {Boolean}
   */
  showCancel = () => {
    return typeof this.props.parentOnCancel === "function";
  };

  /**
   * Resets the form store and calls parent function if applicable
   */
  onCancel = () => {
    //Form store doesn't have any reset function, need to discuss with devs to implement.
    //this.formStore.reset();
    return this.showCancel ? this.props.parentOnCancel() : false;
  };

  render() {
    let { data, parentOnChange, formName, classes } = this.props;
    let { fields, formError, errorMessage } = this.props.formStore;
    let { errorMessageOpen } = this.state;

    return (
      <React.Fragment>
        {errorMessageOpen && (
          <UIAlertDialog
            open={errorMessageOpen}
            errorTitle={formError}
            errorMessage={errorMessage}
            handleClose={this.handleClose}
          />
        )}
        <form action="." onSubmit={this.onSubmit} name={formName} noValidate>
          <Grid container spacing={1} alignItems="flex-start">
            <FormFields
              fields={fields}
              data={data}
              parentOnChange={parentOnChange}
            />
          </Grid>
          <Button
            variant="contained"
            type="submit"
            color="primary"
            fullWidth
            className={classes.apply}
          >
            {this.props.submitButtonText || "Submit"}
          </Button>
        </form>
      </React.Fragment>
    );
  }
}

FormBuilder.propTypes = {
  formStore: PropTypes.object.isRequired,
  submitButtonText: PropTypes.string,
  parentOnChange: PropTypes.func.isRequired,
  parentOnSubmit: PropTypes.func,
  parentOnCancel: PropTypes.func,
  customValidate: PropTypes.func,
  data: PropTypes.object.isRequired,
  formName: PropTypes.string,
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(observer(FormBuilder));
